import type { OverrideReport } from '@homecare/contracts';
import { Button, Card, EmptyState, Eyebrow, PageHeader, Spinner } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useAgencyToday } from '../../lib/agency-day';
import { useApi } from '../../lib/api-hooks';
import { instantText, shiftWeek, weekText } from '../../lib/schedule-time';

/**
 * The weekly override report (§14.3): every rule somebody booked past, who,
 * and the reason they gave — and a count per rule, so a rule overridden every
 * day stands out without reading every line.
 *
 * "Overrides that are never reviewed become the norm." This page is the
 * review. Its week is the agency's Sunday to Saturday, from the API; an empty
 * week says so in words, because an empty report must never be mistaken for
 * a report that failed to load.
 */
export function OverrideReportPage(): ReactNode {
  const api = useApi();
  const today = useAgencyToday();
  const [weekOf, setWeekOf] = useState<string | null>(null);
  const day = weekOf ?? today;

  const report = useQuery({
    queryKey: ['schedule', 'overrides', day],
    queryFn: () =>
      api.get<OverrideReport>(`/v1/schedule/overrides/report${day ? `?weekOf=${day}` : ''}`),
    enabled: day !== null,
    staleTime: 0,
  });
  const data = report.data;

  return (
    <section className="grid gap-6">
      <PageHeader
        title="Override report"
        meta={
          data
            ? `${weekText(data.weekStart, data.weekEnd)} · every rule booked past, and why`
            : 'Every rule booked past, and why.'
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/schedule" className="text-brand hover:text-brand-hover text-sm font-medium">
          ← Back to the board
        </Link>
        <span className="flex-1" />
        {data && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekOf(shiftWeek(data.weekStart, -1))}
            >
              Previous week
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekOf(shiftWeek(data.weekStart, 1))}
            >
              Next week
            </Button>
          </>
        )}
      </div>

      <ErrorNote error={report.error} nextStep="Reload the page to try again." />

      {report.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Loading the week’s overrides…
        </p>
      ) : data ? (
        data.overrides.length === 0 ? (
          <Card>
            <EmptyState
              title="No overrides this week"
              explanation="Nobody booked past a rule between these dates. When someone does, it appears here with the rule, the visit and the reason they gave."
            />
          </Card>
        ) : (
          <>
            <Card>
              <h2 className="text-ink mb-3 font-semibold">By rule</h2>
              <ul className="flex flex-wrap gap-3 text-sm" aria-label="Overrides by rule">
                {data.byCode.map((entry) => (
                  <li key={entry.code} className="border-line rounded-md border px-3 py-2">
                    <span className="text-ink font-semibold">{entry.count}</span>{' '}
                    <span className="text-ink-2">{entry.code}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <ul className="grid gap-3" aria-label="Overrides">
                {data.overrides.map((entry) => (
                  <li key={entry.id} className="border-line grid gap-1 border-b pb-3 last:border-0">
                    <Eyebrow>
                      Rule {entry.rule} · {entry.code}
                    </Eyebrow>
                    <span className="text-ink">“{entry.reason}”</span>
                    <span className="text-ink-3 text-sm">
                      {entry.clientName} · visit {instantText(entry.visitStartsAt, data.timezone)}
                      {entry.caregiverName ? ` · ${entry.caregiverName}` : ' · open shift'} ·
                      overridden by {entry.overriddenByName ?? 'someone'} on{' '}
                      {instantText(entry.createdAt, data.timezone)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )
      ) : null}
    </section>
  );
}
